import {XdSentry}           from "../../libs/sentry/xd-sentry";
import {Types}              from "../../types";
import {isDevXd}            from "../../ui-components/debug/is-dev";
import {PhotoDashboardTabs} from "../../ui-components/photo-dashboard/photo-dashboard";
import {randomId}           from "../../utils/random";

import {addImageReferenceToProductTags} from "./add-image-reference-to-product-tags";
import {getEditableImages}              from "./common/get-editable-images";

export const getGalleryParams = async ({
    ccEditor,
    userData,
    userCreatedFieldSettings  = null,
    allowedGalleryTabs        = null, // response from apis.next.gallery.getAllowedPhotoGalleryTabs, which includes logic to exclude tabs that will have empty content - this does not reflect all available tabs, however, so only using where explicitly requested
    adminTool                 = false
}:{
    ccEditor                  : Types.Common.Tools.CcIntegration.Models.CcEditor,
    userData                  : any,
    userCreatedFieldSettings ?: any,
    allowedGalleryTabs       ?: string[]
    adminTool                ?: boolean
}) => {

    const product = await ccEditor.getProduct();
    const productModel = await product.getProductModel();
    const productItems = await productModel.getAllItems();

    const imageArea = await getImageArea({product, productItems, userData, ccEditor});
    if (!imageArea ) return null;

    //User Created templates, get Property Photos gallery exclusions and filter from list.
    const exclusionSettings = userCreatedFieldSettings?.[imageArea.surfaceName]?.[imageArea.name]?.excludeOrderGallery;

    //Assume if no gallery is selected, that all options are selected.
    if(!imageArea.allowedTabs) {
        imageArea.allowedTabs = [
            "Agent Logo Gallery", "Brand Logo Gallery", "Designation Gallery", "Headshot Gallery", "Property Photos", "Photo Album"
        ];
    }

    if(imageArea.allowedTabs) {
        // set options where property photos are enabled
        if (imageArea.allowedTabs.includes("Property Photos") || imageArea.allowedTabs.includes(PhotoDashboardTabs.propertyPhotos)) {

            // add listings tab if any listings photos provided
            if( userData?.sourceData?.xd?.listing?.pictures?.length ) {
                imageArea.allowedTabs.push(PhotoDashboardTabs.listings);
            }

            // add previous orders if allowed by template
            if ( (adminTool
                    || userData?.sourceData?.xd?.templateOptions?.options?.find(x => x.devKey === "allow_property_photo_previous_order")
                ) && !exclusionSettings?.includes("Previous Orders")
            ) {
                imageArea.allowedTabs.push(PhotoDashboardTabs.previousOrders);
            }

            // add photo services if allowed by template
            if ( ( adminTool
                    || userData?.sourceData?.xd?.templateOptions?.options?.find(x => x.devKey === "allow_property_photo_services")
                ) && !exclusionSettings?.includes("Photo Services")
                && (!allowedGalleryTabs || allowedGalleryTabs.includes("allow_property_photo_services"))
            ) {
                imageArea.allowedTabs.push(PhotoDashboardTabs.photoIntegrations);
            }

            // remove the company gallery tab if not also allowed by template
            if ( ( !adminTool
                    && !userData?.sourceData?.xd?.templateOptions?.options?.find(x => x.devKey === "allow_property_photo_gallery")
                ) || (allowedGalleryTabs && !allowedGalleryTabs.includes("allow_property_photo_gallery"))
                || exclusionSettings?.includes("Company Gallery")
            ) {
                const propertyPhotosIndex = Math.max(imageArea.allowedTabs.indexOf(PhotoDashboardTabs.propertyPhotos), imageArea.allowedTabs.indexOf("Property Photos"));
                if (propertyPhotosIndex >= 0) imageArea.allowedTabs.splice(propertyPhotosIndex, 1);
            }

        }

        // remove the photo album tab if not also allowed by template
        if ((imageArea.allowedTabs.includes("Image Gallery") || imageArea.allowedTabs.includes(PhotoDashboardTabs.orderGallery))
            && ( ( !adminTool
                    && !userData?.sourceData?.xd?.templateOptions?.options?.find(x => x.devKey === "allow_property_photo_upload")
                ) || exclusionSettings?.includes("Upload"))
        ) {
            const photoAlbumIndex = Math.max(imageArea.allowedTabs.indexOf(PhotoDashboardTabs.orderGallery), imageArea.allowedTabs.indexOf("Image Gallery"));
            if (photoAlbumIndex >= 0) imageArea.allowedTabs.splice(photoAlbumIndex, 1);
        }

        // remove the order gallery if the only other tab is the headshot or logo gallery
        if (imageArea.allowedTabs.length === 2
            && (imageArea.allowedTabs.includes(PhotoDashboardTabs.headshots)
                || imageArea.allowedTabs.includes(PhotoDashboardTabs.logos)
                || imageArea.allowedTabs.includes("Agent Logo Gallery")
            )
            && imageArea.allowedTabs.includes("Image Gallery")
        ) {
            imageArea.allowedTabs.splice(imageArea.allowedTabs.indexOf("Image Gallery"), 1);
        }
    }

    return {
        imageArea,
        onApplyImage: (imageArea, assetType, imageId, imagePath, callback = null) => {
            // TODO change callback to async
            applyImageToImageField(imageArea, {
                ccEditor, assetType, imageId, imagePath,
                productItems,
                callback
            });
        },
    };

};

const getImageArea = async ({product, productItems, userData, ccEditor}) => {

    let productItemsFiltered = [];

    const surfaceName = product.currentSurface.name;
    const surfaceWidth = product.currentSurface.width;
    const surfaceHeight = product.currentSurface.height;

    // check if the select image icon button was used
    const designerXDData = await ccEditor.editorEval("typeof window.xdData !== 'undefined' ? window.xdData : null");
    if (designerXDData?.LastSelectImageButtonPosition) {
        if (isDevXd()) console.log("CcEditor.loadCustomGallery found LastSelectImageButtonPosition", designerXDData.LastSelectImageButtonPosition);

        // find the left and top position of the button and translate to coordinates that match the product item positions
        const buttonPosition = designerXDData.LastSelectImageButtonPosition;
        buttonPosition.left *= (surfaceWidth / buttonPosition.canvasWidth);
        buttonPosition.top *= (surfaceHeight / buttonPosition.canvasHeight);
        const buttonIsOverItem = (buttonPosition, item) => {
            /*  Translation rules were difficult to determine. By creating an item, moving to 0,0, adjusting for
                varying scaling factors, found the following to be a reliable determination of actual position:

                w = Source rect width
                l = Source rect left
                s = transform scale x
                △ = transform translate x

                X1 = l + △
                X2 = w * ((1 - s) / 2)

                Actual Left = X1 + X2

            */

            const itemLeft = (item._sourceRectangle._left + item._transform._translateX) + (item._sourceRectangle._width * ((1 - item._transform._scaleX) / 2));
            const itemTop = (item._sourceRectangle._top + item._transform._translateY) + (item._sourceRectangle._height * ((1 - item._transform._scaleY) / 2));
            const itemRight = itemLeft + (item._sourceRectangle._width * item._transform._scaleX);
            const itemBottom = itemTop + (item._sourceRectangle._height * item._transform._scaleY);

            // return true if button position is within bounds
            return (
                buttonPosition.left >= itemLeft &&
                buttonPosition.left <= itemRight &&
                buttonPosition.top >= itemTop &&
                buttonPosition.top <= itemBottom
            );

        };

        // find the image item that the button is placed over
        productItemsFiltered = productItems.filter(item =>
            item._parentContainer.parentComponent._name === surfaceName && // match current surface
            item._name && // must have a name
            item.type === "PlaceholderItem" && // must be an image element
            item._visible === true && // must be visible
            item._locked === false && // must be editable
            // the position of the button within the designer must be contained within the position of
            // the product item
            buttonIsOverItem(buttonPosition, item)
        );

        // if found, prompt the custom gallery modal with the found item
        if (productItemsFiltered.length >= 1) {
            if (isDevXd()) console.log("CcEditor.loadCustomGallery found an image area under the pressed icon button - using for gallery association", [surfaceName, productItemsFiltered[0]]);

            // use the smallest item that is returned - in all cases seen so far, where an icon button overlays
            // multiple buttons, the icon is related to the smaller - the second image has always been a large
            // background image with an icon button in a separate area unique to itself
            const selectedItem = productItemsFiltered.sort(
                (x, y) => x._sourceRectangle._width * x._sourceRectangle._height * x._transform._scaleX > y._sourceRectangle._width * y._sourceRectangle._height * y._transform._scaleX ? 1 : -1
            )[0];

            return {
                name: productItemsFiltered[0]._name,
                allowedTabs: productItemsFiltered[0].allowedTabs,
                surfaceName: surfaceName,
                widthInches: productItemsFiltered[0]._sourceRectangle._width / 72,
                heightInches: productItemsFiltered[0]._sourceRectangle._height / 72,
                linkId: productItemsFiltered[0].linkId,
                photoQualitySettings: userData?.sourceData?.xd?.templateOptions?.photoQualitySettings,
            };
        }

        if (isDevXd()) console.log("CcEditor.loadCustomGallery did not find an image area under the pressed icon button - checking for selected items");

    }

    // check for any selected image items
    const selectedItems = await ccEditor.callEditorMethod("getSelectedItems");
    if (isDevXd()) console.log("CcEditor.loadCustomGallery searching for selectedItems", selectedItems);

    if (selectedItems.length === 1) {

        // filter by ID alone is probably fine, but may as well match to all known rules
        productItemsFiltered = productItems.filter(att =>
            att.id === selectedItems[0].id &&
            att._parentContainer.parentComponent._name === surfaceName && // match current surface
            att._name === selectedItems[0].name && // must have a name
            att.type === "PlaceholderItem" && // must be an image element
            att._visible === true && // must be visible
            att._locked === false // must be editable
        );

        // if found, prompt the custom gallery modal with the found item
        if (productItemsFiltered.length === 1) {
            if (isDevXd()) console.log("CcEditor.loadCustomGallery found a selected image item - using for gallery association", surfaceName, productItemsFiltered[0]);
            return {
                name: productItemsFiltered[0]._name,
                allowedTabs: productItemsFiltered[0].allowedTabs,
                surfaceName: surfaceName,
                widthInches: productItemsFiltered[0]._sourceRectangle._width / 72,
                heightInches: productItemsFiltered[0]._sourceRectangle._height / 72,
                linkId: productItemsFiltered[0].linkId,
            };
        }

    }
    if( !userData?.sourceData?.xd?.templateOptions?.options?.find((o) => o.devKey === "enable_cc_adv_mode")?.value ) {
        // image area should have been evaluated prior - issue an error to Sentry to troubleshoot
        XdSentry.addBreadcrumb({
            category: "custcanv",
            message: "Image selection was prompted without determining the related designer image area.",
            level: XdSentry.Severity.Error,
            data: {
                lastSelectedImageButtonPosition: designerXDData?.LastSelectImageButtonPosition,
                selectedItems: selectedItems,
            }
        });
        XdSentry.captureMessage("Image Selection Error", XdSentry.Severity.Log);

        // stop execution if no images found
        if (isDevXd()) console.log("CcEditor.loadCustomGallery did not find a selected image item");
        return null;
    }
    else {
        let allowedTabs = [];
        if( userData?.sourceData?.xd?.listing?.pictures?.length ) {
            allowedTabs.push(PhotoDashboardTabs.listings);
        }
        allowedTabs.push(PhotoDashboardTabs.headshots, PhotoDashboardTabs.logos);
        if( userData?.sourceData?.xd?.company?.imageGallery?.designations?.length ) {
            allowedTabs.push(PhotoDashboardTabs.designations);
        }
        allowedTabs.push(PhotoDashboardTabs.photoIntegrations);
        return {
            surfaceName,
            allowedTabs,
        };
    }
};

const applyImageToImageField = async (
    imageArea: Types.Common.UiComponents.PhotoDashboard.Models.ImageArea,
    {
        ccEditor,
        assetType,
        imageId,
        imagePath,
        productItems,
        callback,
    },
) => {
    const product = await ccEditor.getProduct();

    // if no image area was selected, then create a new image area
    if (!imageArea?.name) {
        const CustomersCanvas = window["CustomersCanvas"] || null;
        let model = CustomersCanvas.DesignAtoms.ObjectModel;
        let currentSurface = product.currentSurface;
        const placeholder = new model.PlaceholderItem(new model.RectangleF(currentSurface.width/4, currentSurface.height/4, currentSurface.width/2, currentSurface.height/2));
        placeholder.content = new model.ImageItem();
        placeholder.isCoverMode = true;
        placeholder.allowedTabs = imageArea.allowedTabs;
        placeholder.name = `photo-${randomId()}`;
        if (isDevXd()) console.log("CcEditor.applyImageToImageField ImageItem create", placeholder);

        currentSurface.insertItem(placeholder);
        imageArea.name = placeholder.name;
    }

    // set the selected image for the selected image area
    let payload = {surfaces: {}};
    if (!imageArea.linkId) {

        payload.surfaces[imageArea.surfaceName] = {};
        payload.surfaces[imageArea.surfaceName][imageArea.name] = imagePath;

        // write image reference to product tags - imageId may not exist - the image will be registered as a new asset if ID is not set
        await addImageReferenceToProductTags (product, assetType, imageId, imageArea.surfaceName, imageArea.name);

    } else {
        // if the selected image area is a linked image, apply for all other linked image areas
        if (isDevXd()) console.log("CcEditor.applyImageToImageField found link ID", imageArea.linkId, "on image area");

        for(const checkImageArea of getEditableImages({items: productItems, onlyVisibleOrderImages: false})) {
            if (checkImageArea.linkId !== imageArea.linkId) continue;

            if (!payload.surfaces[checkImageArea.surfaceName]) payload.surfaces[checkImageArea.surfaceName] = {};
            payload.surfaces[checkImageArea.surfaceName][checkImageArea.name] = imagePath;

            await addImageReferenceToProductTags (product, assetType, imageId, checkImageArea.surfaceName, checkImageArea.name);
        }

    }

    // add image to the editor
    if (isDevXd()) console.log("CcEditor.applyImageToImageField editor.loadUserInfo payload", payload);
    await ccEditor.callEditorMethod("loadUserInfo", payload);

    // run a callback if provided
    if (callback) callback();

};
